import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { warmup } from '../api';
import StatCard from '../components/common/StatCard';
import RiskBadge from '../components/common/RiskBadge';
import LoadingSkeleton from '../components/common/LoadingSkeleton';
import CheckinSignals from '../components/common/CheckinSignals';
import BulkLogModal from './BulkLogModal';
import TopLoader from '../components/common/TopLoader';

const getCoachSport = () => localStorage.getItem('coachSport') || null;
const getAcademyId = () => localStorage.getItem('academyId') || '';
const getAcademyType = () => localStorage.getItem('academyType') || 'sport';
const norm = (str) => (str || '').trim().toLowerCase();

const Bar = ({ value, color }) => (
  <div className="w-full bg-gray-700/50 rounded-full h-1.5 mt-1 overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-1000 ease-out ${color}`}
      style={{ width: `${value * 10}%` }} />
  </div>
);

const InjuryScorePill = ({ score, riskLevel }) => {
  if (score == null) return null;
  const color = riskLevel === 'red' ? 'text-rose-400' : riskLevel === 'yellow' ? 'text-amber-400' : 'text-emerald-400';
  const bg = riskLevel === 'red' ? 'bg-rose-500/10 border-rose-500/20'
    : riskLevel === 'yellow' ? 'bg-amber-500/10 border-amber-500/20'
      : 'bg-emerald-500/10 border-emerald-500/20';
  return (
    <div className={`rounded-lg px-3 py-1.5 border text-center ${bg}`}>
      <p className={`text-lg font-black ${color}`}>{score}</p>
      <p className="text-[10px] text-gray-500 uppercase font-bold">Injury Risk</p>
    </div>
  );
};

const LiveIndicator = ({ lastUpdated }) => (
  <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-full px-3 py-1">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
    </span>
    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
      Live {lastUpdated && `· ${lastUpdated}`}
    </span>
  </div>
);

const MUSCLE_OPTIONS = ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms', 'Core', 'Full Body'];
const WORKOUT_TYPES = ['Strength', 'Cardio', 'HIIT', 'Flexibility', 'Mobility'];
const DURATIONS = [30, 45, 60, 90];
const INTENSITIES = ['Light', 'Moderate', 'Intense'];
const PROGRESSIONS = ['Lighter', 'Same', 'Heavier'];
const GOAL_PROGRESS = ['On Track', 'Progressing', 'Needs Attention'];

function PillGroup({ options, value, onChange, colors }) {
  const colorMap = colors || {};
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map(opt => {
        const active = value === opt;
        const c = colorMap[opt] || 'purple';
        const activeClass = {
          purple: 'border-purple-500/70 bg-purple-500/15 text-purple-300',
          blue:   'border-blue-500/70 bg-blue-500/15 text-blue-300',
          green:  'border-green-500/70 bg-green-500/15 text-green-300',
          amber:  'border-amber-500/70 bg-amber-500/15 text-amber-300',
          red:    'border-red-500/70 bg-red-500/15 text-red-300',
        }[c];
        return (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors ${active ? activeClass : 'border-gray-700 text-gray-500 hover:border-gray-600'}`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function GymLogModal({ athlete, academyId, onClose, onSaved }) {
  const [muscles, setMuscles] = React.useState([]);
  const [workoutType, setWorkoutType] = React.useState('Strength');
  const [duration, setDuration] = React.useState(60);
  const [intensity, setIntensity] = React.useState('Moderate');
  const [progression, setProgression] = React.useState('Same');
  const [goalProgress, setGoalProgress] = React.useState('On Track');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const toggleMuscle = (mg) =>
    setMuscles(prev => prev.includes(mg) ? prev.filter(x => x !== mg) : [...prev, mg]);

  const handleSave = async () => {
    if (muscles.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/wellness?academy_id=${academyId}`, {
        athlete_name: athlete.name,
        muscle_groups: muscles,
        workout_type: workoutType,
        session_duration: duration,
        intensity,
        progression,
        goal_progress: goalProgress,
        notes: notes.trim() || null,
        logged_by: 'trainer',
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('Session log failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg p-6 my-4"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-white font-black text-lg">Log Session</h3>
            <p className="text-gray-500 text-xs mt-0.5">{athlete.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">✕</button>
        </div>

        <div className="space-y-5">

          {/* Body Part / Muscle Groups */}
          <div>
            <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">
              Body Part Trained <span className="text-red-400">*</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {MUSCLE_OPTIONS.map(mg => (
                <button key={mg} type="button" onClick={() => toggleMuscle(mg)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors ${muscles.includes(mg) ? 'border-blue-500/70 bg-blue-500/15 text-blue-300' : 'border-gray-700 text-gray-500 hover:border-gray-600'}`}>
                  {mg}
                </button>
              ))}
            </div>
            {muscles.length === 0 && <p className="text-gray-600 text-[10px] mt-1.5">Select at least one</p>}
          </div>

          {/* Workout Type */}
          <div>
            <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">Workout Type</p>
            <PillGroup options={WORKOUT_TYPES} value={workoutType} onChange={setWorkoutType} colors={{ Strength: 'purple', Cardio: 'blue', HIIT: 'red', Flexibility: 'green', Mobility: 'amber' }} />
          </div>

          {/* Duration + Intensity side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">Duration</p>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map(d => (
                  <button key={d} type="button" onClick={() => setDuration(d)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors ${duration === d ? 'border-green-500/70 bg-green-500/15 text-green-300' : 'border-gray-700 text-gray-500 hover:border-gray-600'}`}>
                    {d}m
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">Intensity</p>
              <PillGroup options={INTENSITIES} value={intensity} onChange={setIntensity}
                colors={{ Light: 'green', Moderate: 'amber', Intense: 'red' }} />
            </div>
          </div>

          {/* Weight Progression */}
          <div>
            <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">Weight vs Last Session</p>
            <PillGroup options={PROGRESSIONS} value={progression} onChange={setProgression}
              colors={{ Lighter: 'blue', Same: 'amber', Heavier: 'purple' }} />
          </div>

          {/* Goal Progress */}
          <div>
            <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">Goal Progress</p>
            <PillGroup options={GOAL_PROGRESS} value={goalProgress} onChange={setGoalProgress}
              colors={{ 'On Track': 'green', 'Progressing': 'blue', 'Needs Attention': 'red' }} />
          </div>

          {/* Trainer Notes */}
          <div>
            <p className="text-gray-400 text-[11px] font-black uppercase tracking-widest mb-2">
              Trainer Notes <span className="text-gray-600 font-normal normal-case tracking-normal">(optional)</span>
            </p>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="PB hit, form issue, injury flag, anything to remember..."
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/50 transition resize-none text-sm" />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving || muscles.length === 0}
          className="w-full mt-6 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-xl font-black text-sm transition">
          {saving ? 'Saving...' : 'Save Session ✓'}
        </button>
      </div>
    </div>
  );
}

function GymSection({ athletes, insights, checkins, onNavigate, attendance = {}, onMarkAttendance, markingAttendance = {} }) {
  const [logModalAthlete, setLogModalAthlete] = React.useState(null);
  const academyId = getAcademyId();
  const getLatestCheckin = (name) => checkins.find(c => norm(c.athlete_name) === norm(name));

  const checkedIn = athletes.filter(a => getLatestCheckin(a.name)).length;

  return (
    <div className="mb-10">
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-black text-white tracking-tight uppercase">Members</h2>
          <span className="text-gray-500 text-xs font-medium">{athletes.length} members · {checkedIn} checked in</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {athletes.map(athlete => {
          const checkin = getLatestCheckin(athlete.name);
          const insight = insights[athlete.name];
          const risk = insight?.risk || 'unknown';
          const isCheckedIn = !!checkin;

          return (
            <div key={athlete.id}
              className={`bg-gray-800 rounded-2xl p-4 sm:p-5 border transition-all hover:border-gray-600 cursor-pointer ${risk === 'red' ? 'border-rose-500/30' : risk === 'yellow' ? 'border-amber-500/20' : 'border-gray-700'}`}
              onClick={() => onNavigate(`/athlete/${encodeURIComponent(athlete.name)}`)}>

              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-black text-lg shrink-0">
                    {athlete.name[0]}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-black text-white truncate">{athlete.name}</h3>
                    <p className="text-gray-500 text-xs font-bold uppercase mt-0.5">
                      Gym Member{athlete.age ? ` · Age ${athlete.age}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {insight?.score != null && isCheckedIn && (
                    <div className="text-center bg-gray-900 rounded-xl px-3 py-1.5 border border-gray-700">
                      <p className={`text-xl sm:text-2xl font-black ${risk === 'red' ? 'text-rose-400' : risk === 'yellow' ? 'text-amber-400' : 'text-emerald-400'}`}>{insight.score}</p>
                      <p className="text-[10px] text-gray-500 font-bold uppercase">Ready</p>
                    </div>
                  )}
                  <RiskBadge risk={risk} checkedIn={isCheckedIn} />
                </div>
              </div>

              {/* Wellness sliders */}
              {checkin ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[
                    { label: 'Energy', val: checkin.energy, color: 'bg-blue-500' },
                    { label: 'Sleep', val: checkin.sleep, color: 'bg-indigo-500' },
                    { label: 'Soreness', val: checkin.soreness, color: 'bg-rose-500' },
                    { label: 'Mood', val: checkin.mood, color: 'bg-amber-500' },
                  ].map(m => (
                    <div key={m.label} className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/50">
                      <div className="flex justify-between text-[10px] font-bold uppercase text-gray-500 mb-1.5">
                        <span>{m.label}</span><span className="text-gray-400">{m.val}/10</span>
                      </div>
                      <Bar value={m.val} color={m.color} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-900/50 rounded-xl px-4 py-3 mb-4 border border-dashed border-gray-700 flex items-center gap-3">
                  <span className="text-gray-600">⏳</span>
                  <p className="text-gray-500 text-xs">No check-in today — remind {athlete.name.split(' ')[0]}.</p>
                </div>
              )}

              {/* Gym workout summary */}
              {checkin && (checkin.workout_type || checkin.session_duration || checkin.muscle_groups?.length > 0) && (
                <div className="bg-gray-900/60 rounded-xl px-4 py-3 mb-4 border border-purple-500/10 flex flex-wrap gap-3 items-center">
                  {checkin.workout_type && (
                    <span className="text-[10px] font-black uppercase tracking-wider text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-1 rounded-lg">
                      {checkin.workout_type}
                    </span>
                  )}
                  {checkin.session_duration && (
                    <span className="text-[10px] font-bold text-gray-400">{checkin.session_duration} min</span>
                  )}
                  {checkin.muscle_groups?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {checkin.muscle_groups.map(mg => (
                        <span key={mg} className="text-[10px] font-bold text-gray-400 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded-lg">{mg}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Attendance */}
              <div className="flex items-center gap-2 mb-2 flex-wrap" onClick={e => e.stopPropagation()}>
                <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Attendance:</span>
                {(() => {
                  const key = athlete.name.toLowerCase().trim();
                  const status = attendance[key];
                  const loading = markingAttendance[key];
                  return (
                    <>
                      <button onClick={e => { e.stopPropagation(); onMarkAttendance(athlete, 'present'); }} disabled={loading}
                        className={`text-[10px] font-black px-3 py-1 rounded-lg border transition ${status === 'present' ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' : 'border-gray-700 text-gray-600 hover:border-emerald-500/40 hover:text-emerald-400'}`}>
                        ✓ Present
                      </button>
                      <button onClick={e => { e.stopPropagation(); onMarkAttendance(athlete, 'absent'); }} disabled={loading}
                        className={`text-[10px] font-black px-3 py-1 rounded-lg border transition ${status === 'absent' ? 'bg-rose-500/20 border-rose-500/40 text-rose-400' : 'border-gray-700 text-gray-600 hover:border-rose-500/40 hover:text-rose-400'}`}>
                        ✗ Absent
                      </button>
                      {status && <span className={`text-[10px] font-black uppercase tracking-widest ${status === 'present' ? 'text-emerald-400' : 'text-rose-400'}`}>{status === 'present' ? '✓ Marked Present' : '✗ Marked Absent'}</span>}
                      {loading && <span className="text-gray-600 text-[10px]">saving...</span>}
                    </>
                  );
                })()}
              </div>

              <button
                onClick={() => setLogModalAthlete(athlete)}
                className="w-full mt-3 border border-purple-500/40 text-purple-400 hover:bg-purple-500/10 py-2 rounded-xl text-xs font-black transition">
                + Log Session
              </button>

              {insight?.insight && insight.insight !== 'No data yet' && (
                <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-700 mt-2">
                  <p className="text-[10px] text-blue-400 uppercase font-black mb-2">🤖 AI Insight</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{insight.insight}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {logModalAthlete && (
        <GymLogModal
          athlete={logModalAthlete}
          academyId={academyId}
          onClose={() => setLogModalAthlete(null)}
          onSaved={() => window.location.reload()}
        />
      )}
    </div>
  );
}

function SportSection({ sport, athletes, insights, injuryRisks, checkins, onNavigate, skipLock = false, attendance = {}, onMarkAttendance, markingAttendance = {} }) {
  const sessionKey = `unlocked_${sport.toLowerCase()}`;
  const [unlocked, setUnlocked] = useState(() => skipLock || sessionStorage.getItem(sessionKey) === 'true');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  useEffect(() => { if (skipLock) setUnlocked(true); }, [skipLock]);

  const getLatestCheckin = (athleteName) =>
    checkins.find(c => norm(c.athlete_name) === norm(athleteName));

  // Combined risk: worst of readiness risk + injury risk
  const getCombinedRisk = (athleteName) => {
    const readinessRisk = insights[athleteName]?.risk || 'unknown';
    const injuryRisk = injuryRisks[athleteName]?.risk_level || 'unknown';
    // red > yellow > green > unknown
    if (readinessRisk === 'red' || injuryRisk === 'red') return 'red';
    if (readinessRisk === 'yellow' || injuryRisk === 'yellow') return 'yellow';
    if (readinessRisk === 'green' || injuryRisk === 'green') return 'green';
    return readinessRisk;
  };

  const sportRed = athletes.filter(a => getCombinedRisk(a.name) === 'red').length;
  const sportYellow = athletes.filter(a => getCombinedRisk(a.name) === 'yellow').length;
  const checkedIn = athletes.filter(a => getLatestCheckin(a.name)).length;

  const handleUnlock = () => {
    if (password.trim().toLowerCase() === sport.toLowerCase()) {
      sessionStorage.setItem(sessionKey, 'true');
      setUnlocked(true);
      setError('');
      setPassword('');
    } else {
      setError('Incorrect password');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleLock = () => {
    sessionStorage.removeItem(sessionKey);
    setUnlocked(false);
    setPassword('');
  };

  return (
    <div className="mb-10">
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-black text-white tracking-tight uppercase">{sport}</h2>
          <span className="text-gray-500 text-xs font-medium">
            {athletes.length} athletes · {checkedIn} checked in
          </span>
          {skipLock && (
            <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-lg">
              Your Sport
            </span>
          )}
          {unlocked && !skipLock && (
            <button onClick={handleLock}
              className="text-gray-500 hover:text-white text-[10px] font-bold uppercase tracking-widest border border-gray-800 hover:border-gray-600 px-2 py-1 rounded-lg transition-all">
              🔒 Lock
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sportRed > 0 && (
            <span className="bg-rose-500/10 text-rose-400 border border-rose-500/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
              {sportRed} High Risk
            </span>
          )}
          {sportYellow > 0 && (
            <span className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
              {sportYellow} Caution
            </span>
          )}
          {unlocked && !skipLock && (
            <button onClick={() => onNavigate(`/sport/${encodeURIComponent(sport)}`)}
              className="bg-gray-800 hover:bg-gray-700 text-gray-300 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-gray-700 hover:border-gray-500 transition-all">
              Full View →
            </button>
          )}
        </div>
      </div>

      {!unlocked ? (
        <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h3 className="text-white font-bold text-lg mb-1">{sport} Analytics Locked</h3>
          <p className="text-gray-500 text-sm mb-6">Enter the sport name to unlock</p>
          <div className={`flex gap-2 max-w-sm mx-auto ${shake ? 'animate-bounce' : ''}`}>
            <input type="text" value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
              placeholder={`Type "${sport}" to unlock`}
              className="flex-1 bg-gray-900 border border-gray-700 rounded-xl px-4 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500 transition"
            />
            <button onClick={handleUnlock}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-xl font-bold text-sm transition-all">
              Unlock
            </button>
          </div>
          {error && <p className="text-rose-400 text-xs mt-3 font-bold uppercase">{error}</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {athletes.map(athlete => {
            const checkin = getLatestCheckin(athlete.name);
            const insight = insights[athlete.name];
            const injuryData = injuryRisks[athlete.name];
            const isCheckedIn = !!checkin;
            return (
              <div key={athlete.id}
                className={`bg-gray-800 rounded-2xl p-4 sm:p-5 border transition-all hover:border-gray-600 cursor-pointer ${getCombinedRisk(athlete.name) === 'red' ? 'border-rose-500/30'
                    : getCombinedRisk(athlete.name) === 'yellow' ? 'border-amber-500/20'
                      : 'border-gray-700'
                  }`}
                onClick={() => onNavigate(`/athlete/${encodeURIComponent(athlete.name)}`)}>

                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 font-black text-lg shrink-0">
                      {athlete.name[0]}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-lg font-black text-white truncate">{athlete.name}</h3>
                      <p className="text-gray-500 text-xs font-bold uppercase mt-0.5">
                        {athlete.sport}{athlete.age ? ` · Age ${athlete.age}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {insight?.score != null && isCheckedIn && (
                      <div className="text-center bg-gray-900 rounded-xl px-3 py-1.5 border border-gray-700">
                        <p className={`text-xl sm:text-2xl font-black ${getCombinedRisk(athlete.name) === 'red' ? 'text-rose-400'
                            : getCombinedRisk(athlete.name) === 'yellow' ? 'text-amber-400'
                              : 'text-emerald-400'
                          }`}>{insight.score}</p>
                        <p className="text-[10px] text-gray-500 font-bold uppercase">Ready</p>
                      </div>
                    )}
                    <RiskBadge risk={getCombinedRisk(athlete.name)} checkedIn={isCheckedIn} />
                  </div>
                </div>

                {injuryData?.injury_risk_score != null && (
                  <div className="mb-4">
                    <InjuryScorePill score={injuryData.injury_risk_score} riskLevel={injuryData.risk_level} />
                  </div>
                )}

                {checkin ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[
                      { label: 'Energy', val: checkin.energy, color: 'bg-blue-500' },
                      { label: 'Sleep', val: checkin.sleep, color: 'bg-indigo-500' },
                      { label: 'Soreness', val: checkin.soreness, color: 'bg-rose-500' },
                      { label: 'Mood', val: checkin.mood, color: 'bg-amber-500' },
                    ].map(m => (
                      <div key={m.label} className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/50">
                        <div className="flex justify-between text-[10px] font-bold uppercase text-gray-500 mb-1.5">
                          <span>{m.label}</span>
                          <span className="text-gray-400">{m.val}/10</span>
                        </div>
                        <Bar value={m.val} color={m.color} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-900/50 rounded-xl px-4 py-3 mb-4 border border-dashed border-gray-700 flex items-center gap-3">
                    <span className="text-gray-600">⏳</span>
                    <p className="text-gray-500 text-xs">No check-in today — remind {athlete.name.split(' ')[0]}.</p>
                  </div>
                )}

                <CheckinSignals analysis={checkin?.text_analysis} />

                {/* Attendance */}
                <div className="flex items-center gap-2 mb-2 flex-wrap" onClick={e => e.stopPropagation()}>
                  <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Attendance:</span>
                  {(() => {
                    const key = athlete.name.toLowerCase().trim();
                    const status = attendance[key];
                    const loading = markingAttendance[key];
                    return (
                      <>
                        <button
                          onClick={e => { e.stopPropagation(); onMarkAttendance(athlete, 'present'); }}
                          disabled={loading}
                          className={`text-[10px] font-black px-3 py-1 rounded-lg border transition ${status === 'present'
                              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                              : 'border-gray-700 text-gray-600 hover:border-emerald-500/40 hover:text-emerald-400'
                            }`}>
                          ✓ Present
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); onMarkAttendance(athlete, 'absent'); }}
                          disabled={loading}
                          className={`text-[10px] font-black px-3 py-1 rounded-lg border transition ${status === 'absent'
                              ? 'bg-rose-500/20 border-rose-500/40 text-rose-400'
                              : 'border-gray-700 text-gray-600 hover:border-rose-500/40 hover:text-rose-400'
                            }`}>
                          ✗ Absent
                        </button>
                        {status && (
                          <span className={`text-[10px] font-black uppercase tracking-widest ${status === 'present' ? 'text-emerald-400' : 'text-rose-400'
                            }`}>
                            {status === 'present' ? '✓ Marked Present' : '✗ Marked Absent'}
                          </span>
                        )}
                        {loading && <span className="text-gray-600 text-[10px]">saving...</span>}
                      </>
                    );
                  })()}
                </div>

                {/* ACWR + Baseline + AI Insight */}
                <div className="flex flex-col gap-3">
                  {injuryData && (
                    <div className="flex items-center gap-3 text-xs font-bold flex-wrap">
                      <span className="text-gray-500 uppercase tracking-widest text-[10px]">ACWR</span>
                      {injuryData.metrics?.has_acwr && injuryData.acwr > 0 ? (
                        <span className={`px-2 py-0.5 rounded bg-gray-900 border border-gray-700 ${injuryData.acwr > 1.5 ? 'text-rose-400'
                            : injuryData.acwr > 1.3 ? 'text-amber-400'
                              : injuryData.acwr < 0.8 ? 'text-blue-400'
                                : 'text-emerald-400'
                          }`}>{injuryData.acwr}</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500 rounded-full transition-all duration-700"
                              style={{ width: `${Math.min(((injuryData.sessions_28d || 0) / 12) * 100, 95)}%` }}
                            />
                          </div>
                          <span className="text-gray-600 text-[10px] font-bold">
                            {injuryData.sessions_28d || 0}/12 sessions
                          </span>
                        </div>
                      )}
                      {injuryData.deception_flag && (
                        <span className="text-orange-400 text-[10px] font-bold uppercase animate-pulse">
                          ⚠️ Deception Flagged
                        </span>
                      )}
                    </div>
                  )}

                  {/* Baseline Status + Data Trust */}
                  {insight && (
                    <div className="flex items-center gap-3 flex-wrap">
                      {insight.baseline_active ? (
                        <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-lg">
                          Baseline Active ({insight.baseline_days}d)
                        </span>
                      ) : insight.baseline_days > 0 ? (
                        <span className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                          <span className="text-gray-500">Baseline:</span>
                          <div className="w-14 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full transition-all duration-700"
                              style={{ width: `${Math.min((insight.baseline_days / 14) * 100, 95)}%` }} />
                          </div>
                          <span className="text-gray-600">{insight.baseline_days}/14d</span>
                        </span>
                      ) : null}
                      {insight.confidence?.enough_data && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg border ${
                          insight.confidence.score >= 85
                            ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                            : insight.confidence.score >= 70
                              ? 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                              : 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                        }`}>
                          Trust {insight.confidence.score}%
                        </span>
                      )}
                      {insight.deviations?.hrv && (
                        <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded-lg">
                          ❤️ {insight.deviations.heart_rate?.value || '—'} BPM · HRV {insight.deviations.hrv.value}ms
                        </span>
                      )}
                    </div>
                  )}

                  {insight?.insight && insight.insight !== 'No data yet' && (
                    <div className="bg-gray-900/80 rounded-xl p-4 border border-gray-700">
                      <p className="text-[10px] text-blue-400 uppercase font-black mb-2">🤖 AI Insight</p>
                      <p className="text-gray-300 text-sm leading-relaxed">{insight.insight}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [athletes, setAthletes] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [insights, setInsights] = useState({});
  const [injuryRisks, setInjuryRisks] = useState({});
  const [loading, setLoading] = useState(true);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [sentToday, setSentToday] = useState(() => {
    const key = `broadcast_sent_${new Date().toDateString()}`;
    return JSON.parse(localStorage.getItem(key) || '[]');
  });
  const [recoveryData, setRecoveryData] = useState({});
  const [loadingRecovery, setLoadingRecovery] = useState({});
  const [attendance, setAttendance] = useState({});
  const [markingAttendance, setMarkingAttendance] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [showSessionSetup, setShowSessionSetup] = useState(false);
  const [sessionStart, setSessionStart] = useState('17:00');
  const [sessionEnd, setSessionEnd] = useState('19:00');
  const [savingSessionTime, setSavingSessionTime] = useState(false);
  const navigate = useNavigate();
  const prevCheckinsRef = useRef(null);
  const retryTimerRef = useRef(null);
  const coachSport = getCoachSport();
  const academyId = getAcademyId();
  const isGym = getAcademyType() === 'gym';

  const fetchData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const [athletesRes, checkinsRes] = await Promise.allSettled([
        api.get(`/athletes`, { params: { academy_id: academyId } }),
        api.get(`/wellness`, { params: { academy_id: academyId } }),
      ]);

      if (athletesRes.status !== 'fulfilled') throw new Error('Athletes fetch failed');
      const athletesData = athletesRes.value.data;
      const checkinsData = checkinsRes.status === 'fulfilled' ? checkinsRes.value.data : [];

      const newCheckinsRaw = JSON.stringify(checkinsData);
      const dataChanged = newCheckinsRaw !== prevCheckinsRef.current;

      setAthletes(athletesData);
      setCheckins(checkinsData);
      setLoadError(false);
      setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

      if (dataChanged || !isSilent) {
        const insightResults = {};
        const injuryResults = {};
        await Promise.all(
          athletesData.map(async (athlete) => {
            const [insightRes, injuryRes] = await Promise.allSettled([
              api.get(`/ai/insights/${encodeURIComponent(athlete.name)}`, { params: { academy_id: academyId } }),
              api.get(`/ai/injury-risk/${encodeURIComponent(athlete.name)}`, { params: { academy_id: academyId } }),
            ]);
            insightResults[athlete.name] = insightRes.status === 'fulfilled' ? insightRes.value.data : null;
            injuryResults[athlete.name] = injuryRes.status === 'fulfilled' ? injuryRes.value.data : null;
          })
        );
        setInsights(insightResults);
        setInjuryRisks(injuryResults);
        prevCheckinsRef.current = newCheckinsRaw;
      }

      try {
        const attendanceRes = await api.get(`/attendance/today`, {
          params: { academy_id: academyId }
        });
        const attendanceMap = {};
        (attendanceRes.data || []).forEach(a => {
          attendanceMap[a.athlete_name.toLowerCase().trim()] = a.status;
        });
        setAttendance(attendanceMap);
      } catch {
        // attendance fetch failure should not block dashboard
      }

      // Fetch session logging status
      try {
        const statusRes = await api.get(`/wellness/session-status`, {
          params: { academy_id: academyId }
        });
        setSessionStatus(statusRes.data);
        // If no session_time configured yet, prompt setup (only once per session)
        if (!statusRes.data.session_time && !sessionStorage.getItem('session_setup_dismissed')) {
          setShowSessionSetup(true);
        }
      } catch {
        // session status failure should not block dashboard
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [academyId]);

  useEffect(() => {
    if (isGym) { navigate('/gym-dashboard', { replace: true }); return; }
    warmup().then(() => fetchData());
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData, isGym, navigate]);

  useEffect(() => {
    if (loadError && athletes.length === 0) {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => fetchData(true), 3000);
    }
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [loadError, athletes.length, fetchData]);

  const visibleAthletes = coachSport
    ? athletes.filter(a => norm(a.sport) === norm(coachSport))
    : athletes;

  const checkedInToday = visibleAthletes.filter(a =>
    checkins.find(c => norm(c.athlete_name) === norm(a.name))
  ).length;
  const getCombinedRiskTop = (name) => {
    const rr = insights[name]?.risk || 'unknown';
    const ir = injuryRisks[name]?.risk_level || 'unknown';
    if (rr === 'red' || ir === 'red') return 'red';
    if (rr === 'yellow' || ir === 'yellow') return 'yellow';
    if (rr === 'green' || ir === 'green') return 'green';
    return rr;
  };
  const highRiskCount = visibleAthletes.filter(a => getCombinedRiskTop(a.name) === 'red').length;
  const cautionCount = visibleAthletes.filter(a => getCombinedRiskTop(a.name) === 'yellow').length;

  const sportGroups = visibleAthletes.reduce((groups, athlete) => {
    const sport = athlete.sport || 'General';
    if (!groups[sport]) groups[sport] = [];
    groups[sport].push(athlete);
    return groups;
  }, {});

  const handleLogout = () => {
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('unlocked_')) sessionStorage.removeItem(key);
    });
    localStorage.removeItem('role');
    localStorage.removeItem('athleteName');
    localStorage.removeItem('athleteSport');
    localStorage.removeItem('coachSport');
    localStorage.removeItem('parentChildName');
    navigate('/login');
  };

  const buildMessage = (athlete) => {
    const insight = insights[athlete.name];
    const aiText = insight?.insight || insight?.athlete_message;
    const firstName = athlete.name.split(' ')[0];
    const academyName = localStorage.getItem('academyName') || 'Academy';

    let msg = `👋 Hello from *${academyName}*\n\n`;
    msg += `Here's a quick update on *${athlete.name}* from today's session:\n\n`;
    if (aiText && aiText !== 'No data yet') {
      msg += `📊 *Performance Note:*\n${aiText}\n\n`;
    }
    msg += `✅ ${firstName} has been monitored today using AthleteIQ — our performance tracking system.\n\n`;
    msg += `If you have any questions about ${firstName}'s training, feel free to reach out directly.\n\n`;
    msg += `— Coach, ${academyName}`;
    return msg;
  };

  const handleBroadcastSend = (athlete) => {
    if (!athlete.parent_phone) return;
    const msg = buildMessage(athlete);
    const url = `https://wa.me/91${athlete.parent_phone.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
    const key = `broadcast_sent_${new Date().toDateString()}`;
    const updated = [...sentToday, athlete.name];
    setSentToday(updated);
    localStorage.setItem(key, JSON.stringify(updated));
  };

  const handleSendRecovery = async (athlete) => {
    if (!athlete.parent_phone) return;
    const recoveryKey = `recovery_sent_${athlete.name}_${new Date().toDateString()}`;
    if (localStorage.getItem(recoveryKey)) {
      alert(`Recovery message already sent to ${athlete.name.split(' ')[0]}'s parent today.`);
      return;
    }
    setLoadingRecovery(prev => ({ ...prev, [athlete.name]: true }));
    try {
      const res = await api.get(
        `/ai/parent-recovery/${encodeURIComponent(athlete.name)}`,
        { params: { academy_id: academyId } }
      );
      const data = res.data;
      setRecoveryData(prev => ({ ...prev, [athlete.name]: data }));

      let msg = `🚨 *Recovery Alert — ${athlete.name}*\n`;
      msg += `${localStorage.getItem('academyName') || 'Academy'}\n\n`;
      if (data.coach_message) msg += `*Coach's Note:*\n${data.coach_message}\n\n`;
      if (data.exercises?.length > 0) {
        msg += `*Home Recovery Exercises:*\n`;
        data.exercises.forEach((ex, i) => {
          msg += `${i + 1}. *${ex.name}* (${ex.duration})\n`;
          if (ex.how) msg += `   ${ex.how}\n`;
        });
      }
      msg += `\n🔗 View detailed stats: ${window.location.origin}/login\n`;
      msg += `— AthleteIQ`;

      const url = `https://wa.me/91${athlete.parent_phone.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
      localStorage.setItem(recoveryKey, 'true');
    } catch (err) {
      console.error('Recovery fetch failed:', err);
    } finally {
      setLoadingRecovery(prev => ({ ...prev, [athlete.name]: false }));
    }
  };

  const handleMarkAttendance = async (athlete, status) => {
    const key = athlete.name.toLowerCase().trim();
    setMarkingAttendance(prev => ({ ...prev, [key]: true }));
    try {
      await api.post(`/attendance`, {
        academy_id: academyId,
        athlete_name: athlete.name,
        status: status,
        date: new Date().toISOString().split('T')[0],
      });
      setAttendance(prev => ({ ...prev, [key]: status }));
    } catch (err) {
      console.error('Attendance mark failed:', err);
    } finally {
      setMarkingAttendance(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveSessionTime = async () => {
    setSavingSessionTime(true);
    try {
      await api.post(`/wellness/session-time`, {
        start: sessionStart,
        end: sessionEnd,
      }, { params: { academy_id: academyId } });
      setShowSessionSetup(false);
      setSessionStatus(prev => ({
        ...prev,
        session_time: { start: sessionStart, end: sessionEnd },
      }));
    } catch (err) {
      console.error('Failed to save session time:', err);
    } finally {
      setSavingSessionTime(false);
    }
  };

  if (loading && athletes.length === 0) return (
    <div className="min-h-screen bg-gray-950 p-6 flex items-center justify-center">
      <p className="text-gray-400">Loading dashboard...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6 md:p-10">
      <TopLoader loading={loading} />
      <div className="max-w-6xl mx-auto">

        {/* ── Header ── */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h1 className="text-2xl sm:text-4xl font-black tracking-tight">AthleteIQ</h1>
                <LiveIndicator lastUpdated={lastUpdated} />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {coachSport ? (
                  <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">
                    {coachSport} Coach
                  </span>
                ) : (
                  <span className="bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">
                    Admin
                  </span>
                )}
                <span className="bg-gray-800 border border-gray-700 text-gray-400 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                  {localStorage.getItem('academyName') || 'Academy'}
                </span>
              </div>
              <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mt-1">
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>

            <button
              onClick={() => setMenuOpen(o => !o)}
              className="sm:hidden flex flex-col gap-1.5 p-2 rounded-xl bg-gray-800 border border-gray-700 shrink-0 mt-1">
              <span className={`block w-5 h-0.5 bg-gray-400 transition-transform ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-5 h-0.5 bg-gray-400 transition-opacity ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-0.5 bg-gray-400 transition-transform ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>

          <div className="hidden sm:flex gap-2 flex-wrap">
            <Link to="/athletes" className="bg-gray-800 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-700 transition-all">
              {isGym ? 'Manage Members' : 'Manage Athletes'}
            </Link>
            <Link to="/session-planner" className="bg-gray-800 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-700 transition-all">
              📋 Session Planner
            </Link>
            <Link to="/meditation" className="bg-gray-800 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-700 transition-all">
              🧘 Meditate
            </Link>
            <Link to="/messages" className="bg-gray-800 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-700 transition-all">
              💬 Messages
            </Link>
            <button onClick={() => setShowBroadcast(true)}
              className="bg-green-700 hover:bg-green-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold transition-all">
              📲 Broadcast
            </button>
            <button
              onClick={() => {
                const present = visibleAthletes.filter(a => attendance[a.name.toLowerCase().trim()] === 'present');
                if (present.length === 0) {
                  alert('Mark at least one athlete as Present before logging a session.');
                  return;
                }
                setShowBulkModal(true);
              }}
              disabled={visibleAthletes.length === 0}
              className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl text-xs font-bold hover:bg-emerald-500 disabled:bg-gray-700 disabled:text-gray-500 transition-all">
              + Log Session
            </button>
            <button onClick={handleLogout}
              className="text-rose-500 font-bold px-4 py-2.5 rounded-xl text-xs hover:bg-rose-500/10 transition-all">
              Logout
            </button>
          </div>

          {menuOpen && (
            <div className="sm:hidden mt-3 flex flex-col gap-2">
              <Link to="/athletes" className="bg-gray-800 text-white px-5 py-3 rounded-xl text-sm font-bold text-center" onClick={() => setMenuOpen(false)}>
                {isGym ? 'Manage Members' : 'Manage Athletes'}
              </Link>
              <Link to="/session-planner" className="bg-gray-800 text-white px-5 py-3 rounded-xl text-sm font-bold text-center" onClick={() => setMenuOpen(false)}>
                📋 Session Planner
              </Link>
              <Link to="/meditation" className="bg-gray-800 text-white px-5 py-3 rounded-xl text-sm font-bold text-center" onClick={() => setMenuOpen(false)}>
                🧘 Meditate
              </Link>
              <Link to="/messages" className="bg-gray-800 text-white px-5 py-3 rounded-xl text-sm font-bold text-center" onClick={() => setMenuOpen(false)}>
                💬 Messages
              </Link>
              <button onClick={() => { setShowBroadcast(true); setMenuOpen(false); }}
                className="bg-green-700 text-white px-5 py-3 rounded-xl text-sm font-bold text-center">
                📲 Broadcast
              </button>
              <button
                onClick={() => {
                  const present = visibleAthletes.filter(a => attendance[a.name.toLowerCase().trim()] === 'present');
                  if (present.length === 0) {
                    alert('Mark at least one athlete as Present before logging a session.');
                    return;
                  }
                  setMenuOpen(false);
                  setShowBulkModal(true);
                }}
                disabled={visibleAthletes.length === 0}
                className="bg-emerald-600 text-white px-5 py-3 rounded-xl text-sm font-bold disabled:bg-gray-700 disabled:text-gray-500">
                + Log Today's Session
              </button>
              <button onClick={handleLogout}
                className="text-rose-500 font-bold px-5 py-3 rounded-xl text-sm border border-rose-500/20 hover:bg-rose-500/10">
                Logout
              </button>
            </div>
          )}
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          <StatCard label={isGym ? 'Total Members' : (coachSport ? `${coachSport} Athletes` : 'Total Athletes')} value={visibleAthletes.length} color="text-white" />
          <StatCard label={isGym ? 'Checked In Today' : 'Active Today'} value={checkedInToday} color="text-emerald-400" />
          <StatCard label="High Risk" value={highRiskCount} color="text-rose-400" />
          <StatCard label="Caution" value={cautionCount} color="text-amber-400" />
          <StatCard
            label="Absent Today"
            value={Object.values(attendance).filter(s => s === 'absent').length}
            color="text-gray-400"
          />
        </div>

        {/* ── Session Logging Reminder ── */}
        {sessionStatus && sessionStatus.reminder_level !== 'none' && !sessionStatus.logged_today && (
          <div className={`rounded-2xl p-4 mb-6 border flex items-center justify-between gap-4 flex-wrap ${
            sessionStatus.reminder_level === 'urgent'
              ? 'bg-rose-500/10 border-rose-500/30'
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-center gap-3">
              <span className={`relative flex h-3 w-3 shrink-0 ${sessionStatus.reminder_level === 'urgent' ? '' : ''}`}>
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  sessionStatus.reminder_level === 'urgent' ? 'bg-rose-400' : 'bg-amber-400'
                }`} />
                <span className={`relative inline-flex rounded-full h-3 w-3 ${
                  sessionStatus.reminder_level === 'urgent' ? 'bg-rose-500' : 'bg-amber-500'
                }`} />
              </span>
              <div>
                <p className={`text-sm font-black ${
                  sessionStatus.reminder_level === 'urgent' ? 'text-rose-400' : 'text-amber-400'
                }`}>
                  {sessionStatus.reminder_level === 'urgent'
                    ? "Today's session not logged"
                    : "Log today's session"}
                </p>
                <p className="text-gray-500 text-xs">
                  Session was {sessionStatus.session_time?.start}–{sessionStatus.session_time?.end}
                  {sessionStatus.sessions_not_logged > 0 && (
                    <span className="text-rose-400 font-bold ml-2">
                      · {sessionStatus.sessions_not_logged} session{sessionStatus.sessions_not_logged > 1 ? 's' : ''} not logged this week
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const present = visibleAthletes.filter(a => attendance[a.name.toLowerCase().trim()] === 'present');
                if (present.length === 0) {
                  alert('Mark at least one athlete as Present before logging a session.');
                  return;
                }
                setShowBulkModal(true);
              }}
              className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all shrink-0 ${
                sessionStatus.reminder_level === 'urgent'
                  ? 'bg-rose-500 hover:bg-rose-400 text-white'
                  : 'bg-amber-500 hover:bg-amber-400 text-gray-900'
              }`}>
              Log Session Now
            </button>
          </div>
        )}

        {/* ── Athletes ── */}
        {visibleAthletes.length === 0 ? (
          loadError ? (
            <div className="bg-gray-800 rounded-2xl p-10 sm:p-16 text-center border border-dashed border-amber-500/30">
              <div className="inline-flex items-center gap-3 mb-3">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
                <h2 className="text-xl sm:text-2xl font-black text-white">Reconnecting…</h2>
              </div>
              <p className="text-gray-500 mb-6 font-medium text-sm">Couldn't reach the server. Retrying automatically.</p>
              <button onClick={() => fetchData(false)}
                className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-xl font-bold transition-all">
                Retry now
              </button>
            </div>
          ) : (
            <div className="bg-gray-800 rounded-2xl p-10 sm:p-16 text-center border border-dashed border-gray-700">
              <h2 className="text-xl sm:text-2xl font-black text-white mb-2">
                {isGym ? 'No members found' : coachSport ? `No ${coachSport} athletes found` : 'No athletes found'}
              </h2>
              <p className="text-gray-500 mb-6 font-medium text-sm">
                {isGym
                  ? 'Add your gym members to get started.'
                  : coachSport
                    ? `No athletes assigned to ${coachSport} yet. Ask an admin to add them.`
                    : 'Get started by adding your athlete profiles.'}
              </p>
              {!coachSport && (
                <Link to="/athletes"
                  className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-xl font-bold transition-all">
                  Add Athletes
                </Link>
              )}
            </div>
          )
        ) : isGym ? (
          <GymSection
            athletes={visibleAthletes}
            insights={insights}
            checkins={checkins}
            onNavigate={navigate}
            attendance={attendance}
            onMarkAttendance={handleMarkAttendance}
            markingAttendance={markingAttendance}
          />
        ) : (
          <div className="space-y-4">
            {Object.entries(sportGroups).map(([sport, sportAthletes]) => (
              <SportSection
                key={sport}
                sport={sport}
                athletes={sportAthletes}
                insights={insights}
                injuryRisks={injuryRisks}
                checkins={checkins}
                onNavigate={navigate}
                skipLock={!!coachSport}
                attendance={attendance}
                onMarkAttendance={handleMarkAttendance}
                markingAttendance={markingAttendance}
              />
            ))}
          </div>
        )}
      </div>

      {showBulkModal && (
        <BulkLogModal
          athletes={visibleAthletes.filter(a => attendance[a.name.toLowerCase().trim()] === 'present')}
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => fetchData(true)}
        />
      )}

      {showBroadcast && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowBroadcast(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}>

            <div className="flex justify-between items-center p-6 border-b border-gray-700 shrink-0">
              <div>
                <h2 className="text-lg font-black text-white">📲 Parent Broadcast</h2>
                <p className="text-gray-500 text-xs mt-0.5">
                  {visibleAthletes.filter(a => checkins.find(c => norm(c.athlete_name) === norm(a.name))).length} athletes checked in today
                </p>
              </div>
              <button onClick={() => setShowBroadcast(false)} className="text-gray-500 hover:text-white text-xl">✕</button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 space-y-3">
              {visibleAthletes.map(athlete => {
                const checkedIn = !!checkins.find(c => norm(c.athlete_name) === norm(athlete.name));
                const hasPhone = !!athlete.parent_phone;
                const alreadySent = sentToday.includes(athlete.name);
                const insight = insights[athlete.name];
                const risk = getCombinedRiskTop(athlete.name);

                return (
                  <div key={athlete.id} className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white font-black text-sm truncate">{athlete.name}</p>
                        <p className={`text-xs font-bold mt-0.5 ${!checkedIn ? 'text-gray-600'
                            : risk === 'red' ? 'text-rose-400'
                              : risk === 'yellow' ? 'text-amber-400'
                                : 'text-emerald-400'
                          }`}>
                          {!checkedIn ? 'No check-in today'
                            : (insight?.insight || insight?.athlete_message)
                              ? `${(insight.insight || insight.athlete_message).slice(0, 50)}...`
                              : 'No insight yet today'}
                        </p>
                      </div>

                      {alreadySent ? (
                        <span className="text-emerald-400 text-xs font-black shrink-0">✅ Sent</span>
                      ) : !hasPhone ? (
                        <span className="text-gray-600 text-xs font-bold shrink-0">No phone</span>
                      ) : !checkedIn ? (
                        <span className="text-gray-600 text-xs font-bold shrink-0">Skipped</span>
                      ) : risk === 'red' ? (
                        <div className="flex flex-col gap-1.5 shrink-0">
                          <button onClick={() => handleBroadcastSend(athlete)}
                            className="bg-green-600 hover:bg-green-500 text-white text-xs font-black px-3 py-1.5 rounded-xl transition">
                            Send update →
                          </button>
                          <button onClick={() => handleSendRecovery(athlete)}
                            disabled={loadingRecovery[athlete.name]}
                            className="bg-rose-600 hover:bg-rose-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-black px-3 py-1.5 rounded-xl transition">
                            {loadingRecovery[athlete.name] ? 'Loading...' : '🚨 Send recovery'}
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleBroadcastSend(athlete)}
                          className="bg-green-600 hover:bg-green-500 text-white text-xs font-black px-4 py-2 rounded-xl transition shrink-0">
                          Send →
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-gray-700 shrink-0">
              <p className="text-gray-600 text-[10px] text-center">
                Only athletes who checked in today are included · Sent status resets daily
              </p>
            </div>
          </div>
        </div>
      )}
      {/* ── Session Time Setup Modal ── */}
      {showSessionSetup && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => { setShowSessionSetup(false); sessionStorage.setItem('session_setup_dismissed', 'true'); }}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm p-6"
            onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-black text-white mb-1">Set Session Timing</h2>
            <p className="text-gray-500 text-xs mb-5">
              We'll remind you to log sessions if you forget. You can change this anytime.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-gray-500 text-[10px] uppercase tracking-widest font-bold block mb-2">Start Time</label>
                <input
                  type="time"
                  value={sessionStart}
                  onChange={e => setSessionStart(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="text-gray-500 text-[10px] uppercase tracking-widest font-bold block mb-2">End Time</label>
                <input
                  type="time"
                  value={sessionEnd}
                  onChange={e => setSessionEnd(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
            <button
              onClick={handleSaveSessionTime}
              disabled={savingSessionTime}
              className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 text-white font-black py-3 rounded-xl text-sm uppercase tracking-wider transition-all">
              {savingSessionTime ? 'Saving...' : 'Save Session Time'}
            </button>
            <button
              onClick={() => { setShowSessionSetup(false); sessionStorage.setItem('session_setup_dismissed', 'true'); }}
              className="w-full text-gray-600 hover:text-gray-400 text-xs font-bold mt-3 py-2 transition-all">
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;